import { useParams } from "react-router-dom";
import { ApplyShell, Profile } from "./Apply";
import { studentProfilePreview } from "../api";
import { useAsync } from "../useAsync";
import { ErrorBox, Skeleton } from "../ui";

/**
 * One student's own page, as a teacher of this cohort may see it.
 *
 * 🔴 IT RENDERS THE STUDENT'S OWN COMPONENTS AGAINST THE REAL PAYLOAD. The project form's
 * first preview injected console-styled primitives under a caption reading "Exactly what a
 * student sees", so the one claim it made about itself was the one thing it got wrong. This
 * imports `Profile` and `ApplyShell` from the student page and passes the server's own
 * payload, so a divergence is not something a reviewer has to notice.
 *
 * 🔴 OUTSIDE `Layout`, so there is no sidebar and no cohort picker — the console's furniture
 * around a student's page would be the same lie in a different form.
 *
 * ⚠️ NO NEW DISCLOSURE. A teacher with `can_view` already sees this person's team, their
 * marks and their handle on the Students tab; this reorders facts they hold. The server
 * scopes it to this cohort, so it cannot tell them somebody is also on another.
 */
export function StudentPreviewPage() {
  const { courseId, studentId } = useParams();
  const q = useAsync(
    () => studentProfilePreview(courseId ?? "", studentId ?? ""), [courseId, studentId]);

  if (q.loading && !q.data) {
    return <ApplyShell><div className="apply-pad"><Skeleton rows={3} /></div></ApplyShell>;
  }
  if (q.error || !q.data) {
    return (
      <ApplyShell>
        <div className="apply-pad">
          {q.error && <ErrorBox err={q.error} onRetry={q.reload} />}
        </div>
      </ApplyShell>
    );
  }
  const name = q.data.profile.account.display_name || "this student";
  return (
    <ApplyShell>
      {/* ⚠️ A BANNER NAMING WHOSE PAGE IT IS, AND IT IS `status` RATHER THAN `alert`: nothing
          is wrong, and a screen reader interrupting with it would be the same over-reaction
          the roster refusal's role already documents. */}
      <div className="apply-pad stack">
        <p className="alert info" role="status">
          <span className="ico" aria-hidden="true">👁</span>
          <span className="body">
            You are looking at <strong>{name}</strong>’s own page. Nothing here is editable —
            their handle is theirs to set, and the Students tab is where you may change it.
          </span>
        </p>
        {/* ⚠️ `onChanged` CANNOT FIRE: every control that would call it is hidden by
            `readOnly`. It is passed because the component's contract asks for it. */}
        <Profile me={q.data.profile} onChanged={q.reload} readOnly />
      </div>
    </ApplyShell>
  );
}
